import { useQuery } from '@apollo/react-hooks';
import type { FC } from 'react';
import React from 'react';
// We have deprecated unstated. Please use react-sweet-state instead
// eslint-disable-next-line no-restricted-imports
import { Subscribe } from 'unstated';

import { getContentAppearancePublished } from '@confluence/content-appearance';
import type {
	ContentRendererExternalQueryType,
	ContentRendererExternalQueryVariablesType,
} from '@confluence/content-body';
import { ContentRendererExternalQuery } from '@confluence/content-body';
import { Attribution, ErrorBoundary } from '@confluence/error-boundary';
import { FlagsStateContainer } from '@confluence/flags';
import { dangerouslyCreateSafeString } from '@confluence/safe-string';

import type { ContentTypeType } from '../../actions/editor-action-types';
import { ExternalShareContentRendererComponent } from './ExternalShareContentRendererComponent';
import type { ExternalShareContentRendererProps } from './types';

const getADF = (serializedAdf: string) => {
	try {
		return { adf: JSON.parse(serializedAdf) };
	} catch (err) {
		// eslint-disable-next-line no-console
		console.error('Invalid adf document', err);
		return { adfError: dangerouslyCreateSafeString(err.stack).toString() };
	}
};

const ExternalContentRenderer: FC<ExternalShareContentRendererProps> = React.memo(
	({ contentId, scrollTo }) => {
		const { data, error, loading } = useQuery<
			ContentRendererExternalQueryType,
			ContentRendererExternalQueryVariablesType
		>(ContentRendererExternalQuery, {
			skip: !contentId,
			context: {
				single: true,
				contentId,
				allowOnExternalPage: true,
			},
			variables: { contentId: contentId! },
			fetchPolicy: 'cache-and-network',
		});

		if (error) {
			throw error;
		}

		if (loading || !data) {
			return null;
		}

		const content = data.singleContent;

		// When publishing the new page it is possible that for a brief moment the
		// `data.content.nodes` will be empty presumably due to a race condition.
		if (!content) {
			return null;
		}

		const { adf, adfError } = getADF(content.body?.atlas_doc_format?.value || '');
		const contentType = content?.type as ContentTypeType;
		const { appearance } = getContentAppearancePublished(content);
		const revision = content?.version?.confRev || '';

		return (
			<ErrorBoundary attribution={Attribution.EXTERNAL_COLLAB}>
				<Subscribe to={[FlagsStateContainer]}>
					{(flags: FlagsStateContainer) => (
						<ExternalShareContentRendererComponent
							adf={adf}
							adfError={adfError}
							appearance={appearance}
							contentId={contentId}
							contentType={contentType}
							flags={flags}
							scrollTo={scrollTo}
							revision={revision}
						/>
					)}
				</Subscribe>
			</ErrorBoundary>
		);
	},
);

export default ExternalContentRenderer;
